import CustomerLifecycleAPI from '../../api/teletel/customerLifecycle';

const DEFAULT_SETTINGS = Object.freeze({
  available: false,
  enabled: false,
  template_version: null,
  enabled_at: null,
  disabled_at: null,
});

const initialState = () => ({
  loadedAccountId: null,
  accountGeneration: 0,
  requestTokens: {},
  uiRequestCounts: {},
  reportRequestCounts: {},
  transitionRequestCounts: {},
  settingsLoaded: false,
  stagesLoaded: false,
  includesArchivedStages: false,
  settings: { ...DEFAULT_SETTINGS },
  stages: [],
  contacts: {},
  transitions: {},
  transitionMeta: {},
  reports: {},
  uiFlags: {
    isFetchingSettings: false,
    isFetchingStages: false,
    isSavingSettings: false,
    isSavingStage: false,
    isReordering: false,
    transitioningContacts: {},
    isBulkTransitioning: false,
  },
});

const payloadFrom = response => response?.data?.payload ?? response?.data;
const accountKey = rootGetters => String(rootGetters.getCurrentAccountId || '');
let requestSequence = 0;

const prepareAccount = ({ state, commit, rootGetters }) => {
  const currentAccountId = accountKey(rootGetters);
  if (state.loadedAccountId !== currentAccountId) {
    commit('resetForAccount', currentAccountId);
  }
  return {
    accountId: currentAccountId,
    generation: state.accountGeneration,
  };
};

const beginScopedRequest = (context, key) => {
  const scope = prepareAccount(context);
  requestSequence += 1;
  const token = `${scope.accountId}:${scope.generation}:${requestSequence}`;
  context.commit('setRequestToken', { key, token });
  const isAccountCurrent = () =>
    context.state.loadedAccountId === scope.accountId &&
    context.state.accountGeneration === scope.generation;
  const isCurrent = () =>
    isAccountCurrent() && context.state.requestTokens[key] === token;
  const commit = (...args) => {
    if (isCurrent()) context.commit(...args);
  };
  const commitForAccount = (...args) => {
    if (isAccountCurrent()) context.commit(...args);
  };
  return { commit, commitForAccount, isCurrent };
};

const beginUIRequest = (commitForAccount, flag) =>
  commitForAccount('adjustUIRequest', { flag, delta: 1 });
const finishUIRequest = (commitForAccount, flag) =>
  commitForAccount('adjustUIRequest', { flag, delta: -1 });
const invalidateReads = (commitForAccount, keys) =>
  commitForAccount('invalidateRequestTokens', keys);

export const getters = {
  isGranted: (state, _getters, _rootState, rootGetters) => {
    const currentAccountId = rootGetters.getCurrentAccountId;
    const account = rootGetters['accounts/getAccount']?.(currentAccountId);
    return (
      state.loadedAccountId === String(currentAccountId) &&
      account?.teletel_features?.lifecycle === true
    );
  },
  getSettings: (state, localGetters) => ({
    ...state.settings,
    available: localGetters.isAvailable,
    enabled: localGetters.isEnabled,
  }),
  isAvailable: (state, localGetters) =>
    Boolean(localGetters.isGranted && state.settings.available),
  isEnabled: (state, localGetters) =>
    Boolean(localGetters.isAvailable && state.settings.enabled),
  areStagesLoaded: state => state.stagesLoaded,
  isReady: (state, localGetters) =>
    Boolean(localGetters.isEnabled && state.stagesLoaded),
  getStages: state => state.stages,
  getActiveStages: state =>
    state.stages
      .filter(stage => !stage.archived_at)
      .sort((a, b) => a.position - b.position),
  getArchivedStages: state => state.stages.filter(stage => stage.archived_at),
  getStageById: state => id =>
    state.stages.find(stage => String(stage.id) === String(id)) || null,
  getContactLifecycle: state => contactId => state.contacts[contactId] || null,
  getTransitions: state => contactId => state.transitions[contactId] || [],
  getTransitionMeta: state => contactId =>
    state.transitionMeta[contactId] || {},
  getReport: state => report => state.reports[report] || null,
  isReportLoading: state => report =>
    Boolean(state.reportRequestCounts[report]),
  getUIFlags: state => state.uiFlags,
  isTransitioning: state => contactId =>
    Boolean(state.uiFlags.transitioningContacts[contactId]),
};

export const actions = {
  async syncContactUpdate(context, contact) {
    prepareAccount(context);
    if (
      !context.state.settings.available ||
      !context.state.settings.enabled ||
      !Object.prototype.hasOwnProperty.call(contact, 'lifecycle_stage_id')
    ) {
      return undefined;
    }

    const currentKey = `contact-current:${contact.id}`;
    const transitionsKey = `contact-transitions:${contact.id}`;
    const currentObserved =
      Object.prototype.hasOwnProperty.call(
        context.state.contacts,
        contact.id
      ) || Boolean(context.state.requestTokens[currentKey]);
    const historyObserved =
      Object.prototype.hasOwnProperty.call(
        context.state.transitions,
        contact.id
      ) || Boolean(context.state.requestTokens[transitionsKey]);

    // contact.updated is account-wide. Do not turn every lifecycle transition
    // into one GET per connected agent or grow this module's cache for contacts
    // that the agent has never opened. The root contact/conversation stores
    // still consume the event snapshot directly.
    if (!currentObserved && !historyObserved) return undefined;

    if (currentObserved) {
      context.commit('syncContactStageId', {
        contactId: contact.id,
        stageId: contact.lifecycle_stage_id,
      });
    }

    // Invalidate any in-flight read before issuing the authoritative refresh.
    // Otherwise an older page-one response can arrive after this cable event
    // and overwrite the newly observed transition.
    context.commit('invalidateRequestTokens', [currentKey, transitionsKey]);
    const requests = [];
    if (currentObserved) {
      requests.push(context.dispatch('fetchContact', contact.id));
    }
    if (historyObserved) {
      requests.push(
        context.dispatch('fetchTransitions', {
          contactId: contact.id,
          perPage: 5,
        })
      );
    }
    // The event snapshot is still safer than leaving a known stale value if
    // either authoritative refresh is temporarily unavailable.
    const results = await Promise.allSettled(requests);
    const currentResult = currentObserved ? results[0] : undefined;
    return currentResult?.status === 'fulfilled'
      ? currentResult.value
      : undefined;
  },

  async getSettings(context, { force = false } = {}) {
    const { state } = context;
    prepareAccount(context);
    if (state.settingsLoaded && !force) return state.settings;
    const { commit, commitForAccount, isCurrent } = beginScopedRequest(
      context,
      'settings:read'
    );

    beginUIRequest(commitForAccount, 'isFetchingSettings');
    try {
      const response = await CustomerLifecycleAPI.getSettings();
      const settings = payloadFrom(response) || {};
      if (!isCurrent()) return undefined;
      commit('setSettings', settings);
      return settings;
    } finally {
      finishUIRequest(commitForAccount, 'isFetchingSettings');
    }
  },

  async updateSettings(context, enabled) {
    const { commit, commitForAccount, isCurrent } = beginScopedRequest(
      context,
      'settings:write'
    );
    invalidateReads(commitForAccount, ['settings:read']);
    beginUIRequest(commitForAccount, 'isSavingSettings');
    try {
      const response = await CustomerLifecycleAPI.updateSettings(enabled);
      const settings = payloadFrom(response) || {};
      if (!isCurrent()) return undefined;
      commit('setSettings', settings);
      invalidateReads(commitForAccount, ['settings:read']);
      return settings;
    } finally {
      finishUIRequest(commitForAccount, 'isSavingSettings');
    }
  },

  async getStages(context, { includeArchived = false, force = false } = {}) {
    const { state } = context;
    prepareAccount(context);
    if (
      state.stagesLoaded &&
      !force &&
      (!includeArchived || state.includesArchivedStages)
    ) {
      return state.stages;
    }
    const { commit, commitForAccount, isCurrent } = beginScopedRequest(
      context,
      'stages:read'
    );

    beginUIRequest(commitForAccount, 'isFetchingStages');
    try {
      const response = await CustomerLifecycleAPI.getStages({
        includeArchived,
      });
      const stages = payloadFrom(response) || [];
      if (!isCurrent()) return undefined;
      commit('setStages', { stages, includeArchived });
      return stages;
    } finally {
      finishUIRequest(commitForAccount, 'isFetchingStages');
    }
  },

  async createStage(context, stage) {
    const { commit, commitForAccount, isCurrent } = beginScopedRequest(
      context,
      'stages:write'
    );
    invalidateReads(commitForAccount, ['stages:read']);
    beginUIRequest(commitForAccount, 'isSavingStage');
    try {
      const response = await CustomerLifecycleAPI.createStage(stage);
      const created = payloadFrom(response);
      if (!isCurrent()) {
        commitForAccount('markStagesStale');
        return undefined;
      }
      commit('upsertStage', created);
      invalidateReads(commitForAccount, ['stages:read']);
      return created;
    } finally {
      finishUIRequest(commitForAccount, 'isSavingStage');
    }
  },

  async updateStage(context, { id, ...stage }) {
    const { commit, commitForAccount, isCurrent } = beginScopedRequest(
      context,
      'stages:write'
    );
    invalidateReads(commitForAccount, ['stages:read']);
    beginUIRequest(commitForAccount, 'isSavingStage');
    try {
      const response = await CustomerLifecycleAPI.updateStage(id, stage);
      const updated = payloadFrom(response);
      if (!isCurrent()) {
        commitForAccount('markStagesStale');
        return undefined;
      }
      commit('upsertStage', updated);
      invalidateReads(commitForAccount, ['stages:read']);
      return updated;
    } finally {
      finishUIRequest(commitForAccount, 'isSavingStage');
    }
  },

  async archiveStage(context, id) {
    const { commit, commitForAccount, isCurrent } = beginScopedRequest(
      context,
      'stages:write'
    );
    invalidateReads(commitForAccount, ['stages:read']);
    beginUIRequest(commitForAccount, 'isSavingStage');
    try {
      const response = await CustomerLifecycleAPI.archiveStage(id);
      const archived = payloadFrom(response);
      if (!isCurrent()) {
        commitForAccount('markStagesStale');
        return undefined;
      }
      if (archived?.id) commit('upsertStage', archived);
      else commit('markStageArchived', id);
      invalidateReads(commitForAccount, ['stages:read']);
      return archived;
    } finally {
      finishUIRequest(commitForAccount, 'isSavingStage');
    }
  },

  async reorderStages(context, stageIds) {
    const { commit, commitForAccount, isCurrent } = beginScopedRequest(
      context,
      'stages:write'
    );
    invalidateReads(commitForAccount, ['stages:read']);
    beginUIRequest(commitForAccount, 'isReordering');
    try {
      const response = await CustomerLifecycleAPI.reorderStages(stageIds);
      const stages = payloadFrom(response);
      if (!isCurrent()) {
        commitForAccount('markStagesStale');
        return undefined;
      }
      if (Array.isArray(stages)) {
        commit('mergeActiveStages', stages);
      } else {
        commit('applyStageOrder', stageIds);
      }
      invalidateReads(commitForAccount, ['stages:read']);
      return stages;
    } finally {
      finishUIRequest(commitForAccount, 'isReordering');
    }
  },

  async fetchContact(context, contactId) {
    const { commit, isCurrent } = beginScopedRequest(
      context,
      `contact-current:${contactId}`
    );
    const response = await CustomerLifecycleAPI.getContact(contactId);
    const lifecycle = payloadFrom(response);
    if (!isCurrent()) return undefined;
    commit('setContactLifecycle', { contactId, lifecycle });
    return lifecycle;
  },

  async fetchTransitions(context, { contactId, page = 1, perPage = 5 }) {
    const { commit, isCurrent } = beginScopedRequest(
      context,
      `contact-transitions:${contactId}`
    );
    const response = await CustomerLifecycleAPI.getTransitions(contactId, {
      page,
      per_page: perPage,
    });
    const transitions = payloadFrom(response) || [];
    if (!isCurrent()) return undefined;
    commit('setTransitions', {
      contactId,
      transitions,
      meta: response.data?.meta || {},
      append: page > 1,
    });
    return transitions;
  },

  async transitionContact(
    context,
    {
      contactId,
      toStageId,
      expectedFromStageId,
      operationUuid,
      reason,
      conversationId,
    }
  ) {
    const { commit, commitForAccount, isCurrent } = beginScopedRequest(
      context,
      `contact-write:${contactId}`
    );
    invalidateReads(commitForAccount, [
      `contact-current:${contactId}`,
      `contact-transitions:${contactId}`,
    ]);
    commitForAccount('setTransitioning', { contactId, value: true });
    try {
      const response = await CustomerLifecycleAPI.transitionContact(contactId, {
        to_stage_id: toStageId,
        expected_from_stage_id: expectedFromStageId,
        operation_uuid: operationUuid,
        ...(reason ? { reason } : {}),
        ...(conversationId ? { conversation_id: conversationId } : {}),
      });
      const payload = payloadFrom(response) || {};
      if (!isCurrent()) return undefined;
      commit('applyTransition', { contactId, payload });
      invalidateReads(commitForAccount, [
        `contact-current:${contactId}`,
        `contact-transitions:${contactId}`,
      ]);
      commit(
        'contacts/SET_CONTACT_ITEM',
        {
          id: Number(contactId),
          lifecycle_stage_id: payload.contact_lifecycle?.current_stage?.id,
        },
        { root: true }
      );
      commit(
        'conversations/UPDATE_CONVERSATION_CONTACT',
        {
          id: Number(contactId),
          lifecycle_stage_id: payload.contact_lifecycle?.current_stage?.id,
        },
        { root: true }
      );
      return payload;
    } finally {
      commitForAccount('setTransitioning', { contactId, value: false });
    }
  },

  async clearContact(
    context,
    { contactId, expectedFromStageId, operationUuid, reason, conversationId }
  ) {
    const { commit, commitForAccount, isCurrent } = beginScopedRequest(
      context,
      `contact-write:${contactId}`
    );
    invalidateReads(commitForAccount, [
      `contact-current:${contactId}`,
      `contact-transitions:${contactId}`,
    ]);
    commitForAccount('setTransitioning', { contactId, value: true });
    try {
      const response = await CustomerLifecycleAPI.clearContact(contactId, {
        expected_from_stage_id: expectedFromStageId,
        operation_uuid: operationUuid,
        ...(reason ? { reason } : {}),
        ...(conversationId ? { conversation_id: conversationId } : {}),
      });
      const payload = payloadFrom(response) || {};
      if (!isCurrent()) return undefined;
      commit('applyTransition', { contactId, payload });
      invalidateReads(commitForAccount, [
        `contact-current:${contactId}`,
        `contact-transitions:${contactId}`,
      ]);
      commit(
        'contacts/SET_CONTACT_ITEM',
        { id: Number(contactId), lifecycle_stage_id: null },
        { root: true }
      );
      commit(
        'conversations/UPDATE_CONVERSATION_CONTACT',
        { id: Number(contactId), lifecycle_stage_id: null },
        { root: true }
      );
      return payload;
    } finally {
      commitForAccount('setTransitioning', { contactId, value: false });
    }
  },

  async bulkTransition(
    context,
    {
      contactIds,
      toStageId,
      clear = false,
      operationUuid,
      expectedFromStageId,
      reason,
    }
  ) {
    const { commitForAccount, isCurrent } = beginScopedRequest(context, 'bulk');
    beginUIRequest(commitForAccount, 'isBulkTransitioning');
    try {
      const response = await CustomerLifecycleAPI.bulkTransition({
        contact_ids: contactIds,
        ...(clear ? { clear: true } : { to_stage_id: toStageId }),
        operation_uuid: operationUuid,
        ...(reason ? { reason } : {}),
        ...(expectedFromStageId !== undefined
          ? { expected_from_stage_id: expectedFromStageId }
          : {}),
      });
      if (!isCurrent()) return undefined;
      return payloadFrom(response);
    } finally {
      finishUIRequest(commitForAccount, 'isBulkTransitioning');
    }
  },

  async fetchReport(context, { report, endpoint = report, params = {} }) {
    const { commit, commitForAccount, isCurrent } = beginScopedRequest(
      context,
      `report:${report}`
    );
    commit('clearReport', report);
    commitForAccount('adjustReportRequest', { report, delta: 1 });
    try {
      const response = await CustomerLifecycleAPI.getReport(endpoint, params);
      const payload = payloadFrom(response) || {};
      if (!isCurrent()) return undefined;
      commit('setReport', { report, payload });
      return payload;
    } finally {
      commitForAccount('adjustReportRequest', { report, delta: -1 });
    }
  },
};

export const mutations = {
  resetForAccount(state, accountId) {
    const accountGeneration = state.accountGeneration + 1;
    Object.assign(state, initialState(), {
      loadedAccountId: accountId,
      accountGeneration,
    });
  },
  setRequestToken(state, { key, token }) {
    state.requestTokens = { ...state.requestTokens, [key]: token };
  },
  invalidateRequestTokens(state, keys) {
    const requestTokens = { ...state.requestTokens };
    keys.forEach(key => {
      delete requestTokens[key];
    });
    state.requestTokens = requestTokens;
  },
  adjustUIRequest(state, { flag, delta }) {
    const count = Math.max(0, (state.uiRequestCounts[flag] || 0) + delta);
    state.uiRequestCounts = { ...state.uiRequestCounts, [flag]: count };
    state.uiFlags = { ...state.uiFlags, [flag]: count > 0 };
  },
  adjustReportRequest(state, { report, delta }) {
    const count = Math.max(0, (state.reportRequestCounts[report] || 0) + delta);
    state.reportRequestCounts = {
      ...state.reportRequestCounts,
      [report]: count,
    };
  },
  markStagesStale(state) {
    state.stagesLoaded = false;
  },
  setSettings(state, settings) {
    state.settings = { ...DEFAULT_SETTINGS, ...settings };
    state.settingsLoaded = true;
  },
  setStages(state, { stages, includeArchived }) {
    state.stages = [...stages].sort((a, b) => a.position - b.position);
    state.stagesLoaded = true;
    state.includesArchivedStages = includeArchived;
  },
  mergeActiveStages(state, stages) {
    const existingById = new Map(
      state.stages.map(stage => [String(stage.id), stage])
    );
    const active = stages.map(stage => {
      const existing = existingById.get(String(stage.id));
      return {
        ...existing,
        ...stage,
        contacts_count: stage.contacts_count ?? existing?.contacts_count,
      };
    });
    const archived = state.stages.filter(stage => stage.archived_at);
    state.stages = [...active, ...archived].sort(
      (a, b) => a.position - b.position
    );
  },
  upsertStage(state, stage) {
    if (!stage) return;
    if (stage.is_default) {
      state.stages.forEach(item => {
        if (item.id !== stage.id) item.is_default = false;
      });
    }
    const index = state.stages.findIndex(item => item.id === stage.id);
    if (index === -1) state.stages.push(stage);
    else state.stages.splice(index, 1, stage);
    state.stages.sort((a, b) => a.position - b.position);
  },
  markStageArchived(state, id) {
    const stage = state.stages.find(item => String(item.id) === String(id));
    if (stage) stage.archived_at = new Date().toISOString();
  },
  applyStageOrder(state, stageIds) {
    const positions = new Map(stageIds.map((id, index) => [String(id), index]));
    state.stages.forEach(stage => {
      if (positions.has(String(stage.id))) {
        stage.position = positions.get(String(stage.id));
      }
    });
    state.stages.sort((a, b) => a.position - b.position);
  },
  setContactLifecycle(state, { contactId, lifecycle }) {
    state.contacts = { ...state.contacts, [contactId]: lifecycle };
  },
  syncContactStageId(state, { contactId, stageId }) {
    const existing = state.contacts[contactId];
    if (!existing) return;

    const currentStage =
      stageId === null
        ? null
        : state.stages.find(stage => String(stage.id) === String(stageId)) || {
            id: stageId,
          };
    state.contacts = {
      ...state.contacts,
      [contactId]: {
        ...existing,
        classified: stageId !== null,
        current_stage: currentStage,
      },
    };
  },
  setTransitions(state, { contactId, transitions, meta, append = false }) {
    const items = append
      ? [...(state.transitions[contactId] || []), ...transitions].filter(
          (item, index, all) =>
            all.findIndex(candidate => candidate.id === item.id) === index
        )
      : transitions;
    state.transitions = { ...state.transitions, [contactId]: items };
    state.transitionMeta = { ...state.transitionMeta, [contactId]: meta };
  },
  applyTransition(state, { contactId, payload }) {
    if (payload.contact_lifecycle) {
      state.contacts = {
        ...state.contacts,
        [contactId]: payload.contact_lifecycle,
      };
    }
    if (payload.transition) {
      state.transitions = {
        ...state.transitions,
        [contactId]: [
          payload.transition,
          ...(state.transitions[contactId] || []),
        ],
      };
    }
  },
  setReport(state, { report, payload }) {
    state.reports = { ...state.reports, [report]: payload };
  },
  clearReport(state, report) {
    state.reports = { ...state.reports, [report]: null };
  },
  setTransitioning(state, { contactId, value }) {
    const count = Math.max(
      0,
      (state.transitionRequestCounts[contactId] || 0) + (value ? 1 : -1)
    );
    state.transitionRequestCounts = {
      ...state.transitionRequestCounts,
      [contactId]: count,
    };
    state.uiFlags.transitioningContacts = {
      ...state.uiFlags.transitioningContacts,
      [contactId]: count > 0,
    };
  },
  setUIFlag(state, flags) {
    state.uiFlags = { ...state.uiFlags, ...flags };
  },
};

export default {
  namespaced: true,
  state: initialState(),
  getters,
  actions,
  mutations,
};
